import { create } from 'zustand';
import { DEFAULT_TEXT_STYLE } from '@tes/domain';
import type {
  Align,
  DateMode,
  DefaultTextStyle,
  FieldKind,
  InputType,
  PageSize,
  SaveDesignPayload,
  SignerRole,
  Template,
  TemplateStatus,
} from '../lib/types';

/** A placed field in editor state; `uid` is client-only (for React keys/selection). */
export interface EditorField {
  uid: string;
  kind: FieldKind;
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Per-field text style overrides; undefined = inherit defaultTextStyle. */
  font?: string;
  fontSize?: number;
  fontColor?: string;
  align?: Align;
  key?: string;
  label?: string;
  required?: boolean;
  default?: string;
  multiline?: boolean;
  /** The literal text drawn for `kind: 'static'`. */
  text?: string;
  type?: InputType;
  signerRole?: string;
  repeatPerPage?: boolean;
  dateMode?: DateMode;
  maxLen?: number;
  mustCheck?: boolean;
}

interface EditorState {
  templateId: string;
  name: string;
  description: string;
  requiresSignature: boolean;
  /** 1-99 presentation order to the signer; null = unordered (shown last). */
  signingOrder: number | null;
  status: TemplateStatus;
  /** Cache key for the rendered base PDF; changes only when the file is swapped. */
  baseFileKey: string;
  pageSizes: PageSize[];
  defaultTextStyle: DefaultTextStyle;
  roles: SignerRole[];
  fields: EditorField[];
  selectedUid: string | null;
  currentPage: number;
  dirty: boolean;
  /** Variable-library popup (opened from the palette / properties panel). */
  libraryOpen: boolean;
  /** Base-file replacement dialog (opened from the settings panel). */
  baseFileOpen: boolean;

  init: (t: Template) => void;
  setLibraryOpen: (open: boolean) => void;
  setBaseFileOpen: (open: boolean) => void;
  setSettings: (
    patch: Partial<Pick<EditorState, 'name' | 'description' | 'requiresSignature' | 'signingOrder'>>,
  ) => void;
  setStyle: (patch: Partial<DefaultTextStyle>) => void;
  addRole: () => void;
  updateRole: (index: number, patch: Partial<SignerRole>) => void;
  removeRole: (index: number) => void;
  addField: (kind: FieldKind, type?: InputType, varKey?: string) => void;
  updateField: (uid: string, patch: Partial<EditorField>) => void;
  removeField: (uid: string) => void;
  /**
   * Mirror of the API's rename cascade: the server has already rewritten every
   * saved draft placement, so this rewrites the in-memory copies to match and
   * leaves `dirty` alone — nothing new needs saving because of it.
   */
  renameVariableKey: (from: string, to: string) => void;
  /**
   * Drop every placement of a deleted variable. The server refuses to delete a
   * variable with saved placements, so anything found here was unsaved — but
   * whatever is removed marks the design dirty so the next save can't carry a
   * key the library no longer has. Returns how many were removed.
   */
  removeVariablePlacements: (key: string) => number;
  select: (uid: string | null) => void;
  setPage: (page: number) => void;
  markClean: () => void;
  toDesignPayload: () => SaveDesignPayload;
}

const uid = (): string => crypto.randomUUID();

export const useEditor = create<EditorState>((set, get) => ({
  templateId: '',
  name: '',
  description: '',
  requiresSignature: false,
  signingOrder: null,
  status: 'draft',
  baseFileKey: '',
  pageSizes: [],
  defaultTextStyle: DEFAULT_TEXT_STYLE,
  roles: [],
  fields: [],
  selectedUid: null,
  currentPage: 1,
  dirty: false,
  libraryOpen: false,
  baseFileOpen: false,

  setLibraryOpen: (open) => set({ libraryOpen: open }),
  setBaseFileOpen: (open) => set({ baseFileOpen: open }),

  init: (t) =>
    set({
      templateId: t.id,
      name: t.name,
      description: t.description ?? '',
      requiresSignature: t.requiresSignature,
      signingOrder: t.signingOrder ?? null,
      status: t.status,
      baseFileKey: t.baseFileKey,
      pageSizes: t.pageSizes,
      defaultTextStyle: t.defaultTextStyle,
      roles: t.signerRoles.map((r) => ({ ...r })),
      fields: t.fields.map((f) => ({
        uid: uid(),
        kind: f.kind,
        page: f.page,
        x: f.x,
        y: f.y,
        w: f.w,
        h: f.h,
        font: f.font,
        fontSize: f.fontSize,
        fontColor: f.fontColor,
        align: f.align,
        key: f.key,
        multiline: f.multiline,
        // Static text lives on the placement, so it DOES come into editor state
        // — there is no library to read it back from.
        ...(f.kind === 'static' ? { text: f.text } : {}),
        // Variable label/required/default live in the library (read live from the
        // ['variables'] query) — deliberately NOT copied into editor state.
        ...(f.kind === 'input'
          ? {
              label: f.label,
              required: f.required,
              type: f.type,
              signerRole: f.signerRole,
              repeatPerPage: f.repeatPerPage,
              dateMode: f.dateMode,
              maxLen: f.maxLen,
              mustCheck: f.mustCheck,
            }
          : {}),
      })),
      selectedUid: null,
      currentPage: 1,
      dirty: false,
    }),

  setSettings: (patch) => set((s) => ({ ...s, ...patch, dirty: true })),
  setStyle: (patch) =>
    set((s) => ({ defaultTextStyle: { ...s.defaultTextStyle, ...patch }, dirty: true })),

  addRole: () =>
    set((s) => ({
      roles: [...s.roles, { key: `role_${s.roles.length + 1}`, label: `Role ${s.roles.length + 1}` }],
      dirty: true,
    })),
  updateRole: (index, patch) =>
    set((s) => ({
      roles: s.roles.map((r, i) => (i === index ? { ...r, ...patch } : r)),
      dirty: true,
    })),
  removeRole: (index) =>
    set((s) => ({ roles: s.roles.filter((_, i) => i !== index), dirty: true })),

  addField: (kind, type, varKey) => {
    const s = get();
    // A checkbox holds a mark, not a line of text, so it starts square instead
    // of inheriting a text field's width.
    const isCheckbox = type === 'checkbox';
    const field: EditorField = {
      uid: uid(),
      kind,
      page: s.currentPage,
      x: 72,
      y: 72,
      w: isCheckbox ? 24 : kind === 'variable' ? 180 : kind === 'static' ? 240 : 160,
      h: isCheckbox ? 24 : type === 'signature' ? 60 : 30,
      // Variables come from the library — the placement stores only the key
      // (+ multiline). `type` is deliberately NOT copied onto the field: the
      // canvas reads it live from the library, so retyping a variable updates
      // every placement at once instead of leaving stale copies behind.
      ...(kind === 'variable'
        ? { key: varKey, multiline: false }
        : kind === 'static'
          ? // Seeded with placeholder text rather than empty: saveDesign rejects
            // a blank static field, so an empty one couldn't be saved at all.
            { text: 'Fixed text', multiline: false }
          : {
            type,
            signerRole: s.roles[0]?.key,
            // Signer fields default to required — most fields a signer is asked
            // to place ARE mandatory; the author can toggle this off per field.
            required: true,
            repeatPerPage: false,
            ...(type === 'date' ? { dateMode: 'auto' as const } : {}),
            ...(type === 'checkbox' ? { mustCheck: false } : {}),
          }),
    };
    set({ fields: [...s.fields, field], selectedUid: field.uid, dirty: true });
  },

  updateField: (id, patch) =>
    set((s) => ({
      fields: s.fields.map((f) => (f.uid === id ? { ...f, ...patch } : f)),
      dirty: true,
    })),
  removeField: (id) =>
    set((s) => ({
      fields: s.fields.filter((f) => f.uid !== id),
      selectedUid: s.selectedUid === id ? null : s.selectedUid,
      dirty: true,
    })),

  renameVariableKey: (from, to) =>
    set((s) => ({
      fields: s.fields.map((f) =>
        f.kind === 'variable' && f.key === from ? { ...f, key: to } : f,
      ),
    })),
  removeVariablePlacements: (key) => {
    const s = get();
    const keep = s.fields.filter((f) => !(f.kind === 'variable' && f.key === key));
    const removed = s.fields.length - keep.length;
    if (removed > 0) {
      set({
        fields: keep,
        selectedUid: keep.some((f) => f.uid === s.selectedUid) ? s.selectedUid : null,
        dirty: true,
      });
    }
    return removed;
  },

  select: (id) => set({ selectedUid: id }),
  setPage: (page) => set({ currentPage: page, selectedUid: null }),
  markClean: () => set({ dirty: false }),

  toDesignPayload: () => {
    const s = get();
    return {
      defaultTextStyle: s.defaultTextStyle,
      signerRoles: s.roles.map((r) => ({
        key: r.key,
        label: r.label,
        defaultOrder: r.defaultOrder ?? undefined,
      })),
      fields: s.fields.map((f, index) => ({
        kind: f.kind,
        page: f.page,
        x: Math.round(f.x),
        y: Math.round(f.y),
        w: Math.round(f.w),
        h: Math.round(f.h),
        origin: 'top-left',
        unit: 'pt',
        sortIndex: index,
        // per-field style overrides (either kind); omitted = inherit default
        font: f.font,
        fontSize: f.fontSize,
        fontColor: f.fontColor,
        align: f.align,
        ...(f.kind === 'variable'
          ? {
              // label/required/default live in the library — send only the key.
              key: f.key,
              multiline: f.multiline,
            }
          : f.kind === 'static'
          ? { text: f.text, multiline: f.multiline }
          : {
              type: f.type,
              signerRole: f.signerRole,
              required: f.required,
              repeatPerPage: f.repeatPerPage,
              ...(f.type === 'date' ? { dateMode: f.dateMode } : {}),
              ...(f.type === 'text' ? { maxLen: f.maxLen } : {}),
              ...(f.type === 'checkbox' ? { mustCheck: f.mustCheck } : {}),
            }),
      })),
    };
  },
}));
